export type EmergencyItemCategory = 'essential' | 'document' | 'utility' | 'distractor';

export interface EmergencyBagItem {
  id: string;
  name: string;
  category: EmergencyItemCategory;
  scoreRuleId: string;
  points: number;
}

export const EMERGENCY_BAG_ITEMS: EmergencyBagItem[] = [
  { id: 'air_mineral', name: 'Air Mineral', category: 'essential', scoreRuleId: 'zone3_essential', points: 5 },
  { id: 'senter', name: 'Senter', category: 'essential', scoreRuleId: 'zone3_essential', points: 5 },
  { id: 'obat_p3k', name: 'Obat-obatan P3K', category: 'essential', scoreRuleId: 'zone3_essential', points: 5 },
  { id: 'peluit', name: 'Peluit', category: 'essential', scoreRuleId: 'zone3_essential', points: 5 },
  { id: 'masker', name: 'Masker', category: 'essential', scoreRuleId: 'zone3_essential', points: 5 },
  { id: 'makanan_instan', name: 'Makanan Instan', category: 'essential', scoreRuleId: 'zone3_essential', points: 5 },
  { id: 'pakaian_ganti', name: 'Pakaian Berganti', category: 'essential', scoreRuleId: 'zone3_essential', points: 5 },
  { id: 'selimut_tipis', name: 'Selimut Tipis', category: 'essential', scoreRuleId: 'zone3_essential', points: 5 },

  { id: 'dokumen_penting', name: 'Dokumen Penting', category: 'document', scoreRuleId: 'zone3_documents', points: 3 },
  { id: 'ktp_kk', name: 'KTP / KK', category: 'document', scoreRuleId: 'zone3_documents', points: 3 },
  { id: 'ijazah', name: 'Ijazah', category: 'document', scoreRuleId: 'zone3_documents', points: 3 },
  { id: 'uang_tunai', name: 'Uang Tunai', category: 'document', scoreRuleId: 'zone3_documents', points: 3 },

  { id: 'powerbank', name: 'Powerbank', category: 'utility', scoreRuleId: 'zone3_utility', points: 2 },
  { id: 'charger_kabel', name: 'Charger Kabel', category: 'utility', scoreRuleId: 'zone3_utility', points: 2 },
  { id: 'jas_hujan', name: 'Jas Hujan', category: 'utility', scoreRuleId: 'zone3_utility', points: 2 },
  { id: 'payung', name: 'Payung', category: 'utility', scoreRuleId: 'zone3_utility', points: 2 },
  { id: 'tali_serbaguna', name: 'Tali Serbaguna', category: 'utility', scoreRuleId: 'zone3_utility', points: 2 },
  { id: 'pisau_lipat', name: 'Pisau Lipat', category: 'utility', scoreRuleId: 'zone3_utility', points: 2 },
  { id: 'botol_minum', name: 'Botol Minum', category: 'utility', scoreRuleId: 'zone3_utility', points: 2 },
  { id: 'perlengkapan_mandi', name: 'Perlengkapan Mandi', category: 'utility', scoreRuleId: 'zone3_utility', points: 2 },

  { id: 'buku_catatan', name: 'Buku Catatan', category: 'distractor', scoreRuleId: 'zone3_distractor', points: -2 },
  { id: 'buku_pelajaran', name: 'Buku Pelajaran', category: 'distractor', scoreRuleId: 'zone3_distractor', points: -2 },
  { id: 'boneka_kecil', name: 'Boneka Kecil', category: 'distractor', scoreRuleId: 'zone3_distractor', points: -2 },
  { id: 'skincare', name: 'Skincare', category: 'distractor', scoreRuleId: 'zone3_distractor', points: -2 },
  { id: 'headset', name: 'Headset', category: 'distractor', scoreRuleId: 'zone3_distractor', points: -2 },
  { id: 'perhiasan', name: 'Perhiasan', category: 'distractor', scoreRuleId: 'zone3_distractor', points: -2 },
  { id: 'kamera_dslr', name: 'Kamera DSLR', category: 'distractor', scoreRuleId: 'zone3_distractor', points: -2 },
  { id: 'speaker', name: 'Speaker', category: 'distractor', scoreRuleId: 'zone3_distractor', points: -2 },
  { id: 'parfum', name: 'Parfum', category: 'distractor', scoreRuleId: 'zone3_distractor', points: -2 },
  { id: 'jam_mewah', name: 'Jam Tangan Mewah', category: 'distractor', scoreRuleId: 'zone3_distractor', points: -2 },
];

export const EMERGENCY_BAG_MAX_SELECT = 10;
export const EMERGENCY_SUPPORT_REQUIRED = 2;

const EMERGENCY_BAG_ITEM_BY_ID = new Map(
  EMERGENCY_BAG_ITEMS.map((item) => [item.id, item] as const),
);

export const EMERGENCY_ESSENTIAL_IDS = EMERGENCY_BAG_ITEMS
  .filter((i) => i.category === 'essential')
  .map((i) => i.id);

export interface EmergencyBagProgress {
  total: number;
  essential: number;
  support: number;
}

export function getEmergencyBagProgress(selectedIds: string[]): EmergencyBagProgress {
  let essential = 0;
  let support = 0;
  for (const id of selectedIds) {
    const item = getEmergencyItemById(id);
    if (!item) continue;
    if (item.category === 'essential') essential += 1;
    else if (item.category === 'document' || item.category === 'utility') support += 1;
  }
  return { total: selectedIds.length, essential, support };
}

export function calculateEmergencyBagScore(selectedIds: string[]): number {
  return selectedIds
    .map((id) => getEmergencyItemById(id))
    .filter((i): i is EmergencyBagItem => !!i)
    .reduce((sum, i) => sum + i.points, 0);
}

export function getEmergencyItemById(id: string): EmergencyBagItem | undefined {
  return EMERGENCY_BAG_ITEM_BY_ID.get(id);
}

export function shuffleEmergencyBagItems(
  source: readonly EmergencyBagItem[] = EMERGENCY_BAG_ITEMS,
): EmergencyBagItem[] {
  const copy = source.slice();
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const current = copy[i];
    const swap = copy[j];
    if (current === undefined || swap === undefined) continue;
    copy[i] = swap;
    copy[j] = current;
  }
  return copy;
}

export function isGoodEmergencyItem(item: EmergencyBagItem): boolean {
  return item.category !== 'distractor';
}

export function isIdealEmergencyBagSelection(selectedIds: string[]): boolean {
  const progress = getEmergencyBagProgress(selectedIds);
  if (progress.total !== EMERGENCY_BAG_MAX_SELECT) return false;
  if (progress.essential < EMERGENCY_ESSENTIAL_IDS.length) return false;
  if (progress.support < EMERGENCY_SUPPORT_REQUIRED) return false;

  return selectedIds.every((id) => {
    const item = getEmergencyItemById(id);
    return !!item && item.category !== 'distractor';
  });
}

/** 10 barang non-pengecoh cukup untuk lanjut. Komposisi 8+2 hanya memengaruhi skor/pesan, tidak mengunci Selesai. */
export function validateEmergencyBagSelection(selectedIds: string[]): {
  valid: boolean;
  message: string;
} {
  if (selectedIds.length !== EMERGENCY_BAG_MAX_SELECT) {
    const remaining = EMERGENCY_BAG_MAX_SELECT - selectedIds.length;
    return {
      valid: false,
      message: remaining > 0
        ? `Pilih ${remaining} barang lagi (total 10 wajib).`
        : 'Pilihan melebihi batas.',
    };
  }

  const items = selectedIds
    .map((id) => getEmergencyItemById(id))
    .filter((i): i is EmergencyBagItem => !!i);

  if (items.length !== EMERGENCY_BAG_MAX_SELECT) {
    return { valid: false, message: 'Pilihan tidak valid.' };
  }

  if (items.some((i) => i.category === 'distractor')) {
    return {
      valid: false,
      message: 'Barang ini tidak mendesak. Membawa terlalu banyak beban bisa memperlambat evakuasimu.',
    };
  }

  return { valid: true, message: '' };
}
